/**
 * Mock seed data for the B1.11 BusinessDashboard.
 *
 * All strings are Turkish-first; translation keys remain in i18n JSONs and are
 * resolved by the smart wrapper. Money values are in kuruş (minor units).
 */
import type {
  DashboardAiInsight,
  DashboardOccupancyBreakdownEntry,
  DashboardPeriod,
  DashboardQuickAction,
  DashboardReview,
  DashboardStaffLeaderboardEntry,
  DashboardStat,
  DashboardUpcomingBooking,
  RevenueChartPoint,
} from './types';

const BASE_SEED = 20260414;

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Deterministic revenue series generator. The curve is gently upward with a
 * weekly seasonal dip on Sundays (weekday=0) and a mid-week peak (Thursday=4).
 */
export function buildRevenueSeries(
  period: DashboardPeriod,
  referenceDate: Date = new Date('2026-04-14T10:00:00+03:00'),
): RevenueChartPoint[] {
  const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
  const rand = seededRandom(BASE_SEED + days);
  const series: RevenueChartPoint[] = [];
  const baseMinor = 85_00_00;

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(referenceDate);
    d.setDate(d.getDate() - i);
    const weekday = d.getDay();
    const seasonal =
      weekday === 0 ? 0.68 : weekday === 4 ? 1.22 : weekday === 6 ? 1.12 : 1.0;
    const noise = 0.85 + rand() * 0.3;
    const growth = 1 + (days - i) * 0.004;
    const valueMinor = Math.round(baseMinor * seasonal * noise * growth);
    series.push({ date: toIsoDate(d), valueMinor });
  }
  return series;
}

export const DASHBOARD_STATS_SEED: readonly DashboardStat[] = [
  {
    key: 'revenue',
    icon: 'revenue',
    labelKey: 'revenue',
    value: '₺24.850',
    trend: 'up',
    trendValue: '+12,4%',
    trendCompareKey: 'revenue',
  },
  {
    key: 'bookings',
    icon: 'bookings',
    labelKey: 'bookings',
    value: '38',
    trend: 'up',
    trendValue: '+6',
    trendCompareKey: 'bookings',
  },
  {
    key: 'occupancy',
    icon: 'occupancy',
    labelKey: 'occupancy',
    value: '%87',
    trend: 'up',
    trendValue: '+4,1%',
    trendCompareKey: 'occupancy',
  },
  {
    key: 'rating',
    icon: 'rating',
    labelKey: 'rating',
    value: '4,8',
    trend: 'neutral',
    trendValue: '±0,0',
    trendCompareKey: 'ratingMonth',
  },
  {
    key: 'avg_ticket',
    icon: 'avg_ticket',
    labelKey: 'avgTicket',
    value: '₺654',
    trend: 'down',
    trendValue: '-2,3%',
    trendCompareKey: 'avgTicket',
  },
  {
    key: 'new_customers',
    icon: 'new_customers',
    labelKey: 'newCustomers',
    value: '7',
    trend: 'up',
    trendValue: '+3',
    trendCompareKey: 'newCustomers',
  },
];

export const DASHBOARD_AI_INSIGHTS_SEED: readonly DashboardAiInsight[] = [
  {
    id: 'ai-1',
    kind: 'capacity_warning',
    severity: 'warning',
    title: 'Cumartesi öğleden sonra dolduruluk %98',
    description:
      'Önümüzdeki cumartesi 14:00–18:00 arası neredeyse dolu. Ayşe Y. için +1 kuaför ataması önerilir.',
    ctaLabel: 'Vardiya Aç',
    ctaHref: '/staff/shifts',
    producedAt: '2026-04-14T09:42:00+03:00',
  },
  {
    id: 'ai-2',
    kind: 'churn_risk',
    severity: 'info',
    title: '12 müşteri 60+ gündür gelmedi',
    description:
      'Bu müşterilerin toplam yıllık değeri ₺18.400. Kişiselleştirilmiş %15 indirim kampanyası otomatik hazır.',
    ctaLabel: 'Kampanyayı Gözden Geçir',
    ctaHref: '/customers?segment=at_risk',
    producedAt: '2026-04-14T08:15:00+03:00',
  },
  {
    id: 'ai-3',
    kind: 'revenue_opportunity',
    severity: 'info',
    title: 'Mama paket satışı %34 artırılabilir',
    description:
      'Son 30 günde kuaför sonrası müşterilerin %61\'i mama almadan çıkıyor. Kasa ekranına öneri ekleyin.',
    ctaLabel: 'Kasa Ayarları',
    ctaHref: '/settings/cashier',
    producedAt: '2026-04-14T07:30:00+03:00',
  },
];

export const DASHBOARD_UPCOMING_BOOKINGS_SEED: readonly DashboardUpcomingBooking[] = [
  {
    id: 'bk-1',
    customerName: 'Ayşe Yılmaz',
    petName: 'Boncuk',
    petSpecies: 'dog',
    serviceName: 'Full Grooming',
    whenDisplay: '11:00 · Bugün',
    statusLabel: 'Onaylı',
    status: 'confirmed',
  },
  {
    id: 'bk-2',
    customerName: 'Mehmet Kara',
    petName: 'Pamuk',
    petSpecies: 'cat',
    serviceName: 'Tırnak Bakımı',
    whenDisplay: '11:30 · Bugün',
    statusLabel: 'Bekliyor',
    status: 'pending',
  },
  {
    id: 'bk-3',
    customerName: 'Elif Demir',
    petName: 'Luna',
    petSpecies: 'dog',
    serviceName: 'Aşı — 2. doz',
    whenDisplay: '14:15 · Bugün',
    statusLabel: 'Onaylı',
    status: 'confirmed',
  },
  {
    id: 'bk-4',
    customerName: 'Ali Korkmaz',
    petName: 'Duman',
    petSpecies: 'cat',
    serviceName: 'Kontrol Muayene',
    whenDisplay: '15:45 · Bugün',
    statusLabel: 'Check-in',
    status: 'checked_in',
  },
  {
    id: 'bk-5',
    customerName: 'Zeynep Aksoy',
    petName: 'Maya',
    petSpecies: 'dog',
    serviceName: 'Eğitim — Grup',
    whenDisplay: '17:00 · Bugün',
    statusLabel: 'Onaylı',
    status: 'confirmed',
  },
];

export const DASHBOARD_REVIEWS_SEED: readonly DashboardReview[] = [
  {
    id: 'rv-1',
    customerName: 'Ceren Öztürk',
    score: 5,
    comment:
      'Boncuk\'u gördüğüm en iyi şekilde teslim ettiler. Ayşe hanım harika bir iş çıkardı.',
    dateDisplay: '2 saat önce',
  },
  {
    id: 'rv-2',
    customerName: 'Kemal Aydın',
    score: 4,
    comment:
      'Temiz salon, nazik personel. Randevu saatinde 10 dakika gecikme oldu.',
    dateDisplay: '1 gün önce',
  },
  {
    id: 'rv-3',
    customerName: 'Selin Baran',
    score: 5,
    comment: 'Aşı randevusu hızlı ve profesyoneldi. Teşekkürler.',
    dateDisplay: '2 gün önce',
  },
];

export const DASHBOARD_STAFF_SEED: readonly DashboardStaffLeaderboardEntry[] = [
  {
    staffId: 'stf-1',
    staffName: 'Ayşe Yıldız',
    avatarUrl: null,
    role: 'Baş Kuaför',
    completedCount: 42,
    revenueMinor: 9_84_00_00,
    avgRating: 4.9,
  },
  {
    staffId: 'stf-2',
    staffName: 'Mert Çelik',
    avatarUrl: null,
    role: 'Kuaför',
    completedCount: 31,
    revenueMinor: 6_72_00_00,
    avgRating: 4.7,
  },
  {
    staffId: 'stf-3',
    staffName: 'Deniz Kaya',
    avatarUrl: null,
    role: 'Veteriner',
    completedCount: 18,
    revenueMinor: 5_45_00_00,
    avgRating: 4.8,
  },
];

export const DASHBOARD_OCCUPANCY_SEED: readonly DashboardOccupancyBreakdownEntry[] = [
  { serviceKey: 'grooming_full', serviceLabel: 'Full Grooming', utilisation: 0.94, bookings: 24 },
  { serviceKey: 'grooming_bath', serviceLabel: 'Banyo & Tarama', utilisation: 0.78, bookings: 18 },
  { serviceKey: 'vet_checkup', serviceLabel: 'Kontrol Muayene', utilisation: 0.62, bookings: 9 },
  { serviceKey: 'vet_vaccine', serviceLabel: 'Aşı', utilisation: 0.48, bookings: 7 },
  { serviceKey: 'training_group', serviceLabel: 'Grup Eğitimi', utilisation: 0.71, bookings: 5 },
];

export const DASHBOARD_QUICK_ACTIONS_SEED: readonly DashboardQuickAction[] = [
  { key: 'new_appointment', labelKey: 'newAppointment', icon: 'calendar', href: '/calendar?new=1' },
  { key: 'open_cashier', labelKey: 'openCashier', icon: 'cashier', href: '/cashier' },
  { key: 'day_close', labelKey: 'dayClose', icon: 'reports', href: '/day-close' },
  { key: 'add_customer', labelKey: 'addCustomer', icon: 'customers', href: '/customers?new=1' },
  { key: 'inventory', labelKey: 'inventory', icon: 'inventory', href: '/inventory' },
  { key: 'staff', labelKey: 'staff', icon: 'staff', href: '/staff' },
  { key: 'reports', labelKey: 'reports', icon: 'reports', href: '/reports' },
];
